import React, { useState } from 'react';
import {
  TextField,
  Box,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { pip_re, apt_re } from './utils';

export default function PackageDependenciesField({
  pip_raw,
  apt_raw,
  pip_error,
  apt_error,
  onChange,
  onErrorChange,
  edit,
}) {
  // Always start collapsed by default
  const [pip_expanded, setPipExpanded] = useState(false);
  const [apt_expanded, setAptExpanded] = useState(false);

  const handlePipChange = (event) => {
    const value = event.target.value;
    const error = !pip_re.test(value);
    onChange({ pip_raw: value, pip: value.split('\n') });
    onErrorChange({ pip_error: error });
  };

  const handleAptChange = (event) => {
    const value = event.target.value;
    const error = !apt_re.test(value);
    onChange({ apt_raw: value, apt: value.split('\n') });
    onErrorChange({ apt_error: error });
  };

  const getLineCount = (text) => {
    if (!text) return 0;
    return text.split('\n').filter((line) => line.trim()).length;
  };

  const getPreview = (text, maxLines = 2) => {
    if (!text) return '';
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length <= maxLines) return text;
    return lines.slice(0, maxLines).join('\n') + '\n...';
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant='body1' fontWeight='medium'>
                pip
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!pip_expanded && pip_raw && (
                  <Typography variant='caption' color='text.secondary'>
                    {getLineCount(pip_raw)} line{getLineCount(pip_raw) !== 1 ? 's' : ''}
                  </Typography>
                )}
                <IconButton
                  size='small'
                  onClick={() => setPipExpanded(!pip_expanded)}
                  disabled={edit}
                >
                  {pip_expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            {pip_expanded ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                value={pip_raw}
                onChange={handlePipChange}
                disabled={edit}
                error={pip_error}
                helperText={
                  pip_error ? 'Invalid character specified' : undefined
                }
                placeholder='Enter pip packages, one per line'
              />
            ) : pip_raw ? (
              <Box
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  maxHeight: '60px',
                  overflow: 'auto',
                  cursor: 'pointer',
                }}
                onClick={() => !edit && setPipExpanded(true)}
              >
                <Typography
                  variant='body2'
                  component='pre'
                  sx={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {getPreview(pip_raw)}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => !edit && setPipExpanded(true)}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ fontStyle: 'italic' }}
                >
                  Click to add pip packages
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid size={12}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant='body1' fontWeight='medium'>
                apt
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {!apt_expanded && apt_raw && (
                  <Typography variant='caption' color='text.secondary'>
                    {getLineCount(apt_raw)} line{getLineCount(apt_raw) !== 1 ? 's' : ''}
                  </Typography>
                )}
                <IconButton
                  size='small'
                  onClick={() => setAptExpanded(!apt_expanded)}
                  disabled={edit}
                >
                  {apt_expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            {apt_expanded ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                value={apt_raw}
                onChange={handleAptChange}
                disabled={edit}
                error={apt_error}
                helperText={
                  apt_error ? 'Invalid character specified' : undefined
                }
                placeholder='Enter apt packages, one per line'
              />
            ) : apt_raw ? (
              <Box
                sx={{
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  maxHeight: '60px',
                  overflow: 'auto',
                  cursor: 'pointer',
                }}
                onClick={() => !edit && setAptExpanded(true)}
              >
                <Typography
                  variant='body2'
                  component='pre'
                  sx={{
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {getPreview(apt_raw)}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'grey.50',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => !edit && setAptExpanded(true)}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ fontStyle: 'italic' }}
                >
                  Click to add apt packages
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

